import React, {useMemo, useRef, useEffect} from 'react';
import {FaIcon} from "../../FaIcon";
import {
    CHANNEL_ALL, CHANNEL_PAID_MEDIA, CHANNEL_GA4, CHANNEL_ECOMMERCE, CHANNEL_BU,
    CATEGORY_GOALS, CATEGORY_BUDGET, CATEGORY_PERFORMANCE, CATEGORY_CONVERSIONS,
    CATEGORY_VIDEO, CATEGORY_SOCIAL,
    CATEGORY_FUNDS, CATEGORY_ATTRIBUTES, CATEGORY_STATUS, CATEGORY_CUSTOM,
} from "../columns_metadata";

const CHANNEL_OPTIONS = (t) => ([
    {value: CHANNEL_ALL, label: t('config_columns_modal.channels.all', 'Todos los canales')},
    {value: CHANNEL_PAID_MEDIA, label: t('config_columns_modal.channels.paid_media', 'Paid Media')},
    {value: CHANNEL_GA4, label: 'GA4'},
    {value: CHANNEL_ECOMMERCE, label: 'Ecommerce'},
    {value: CHANNEL_BU, label: t('config_columns_modal.channels.bu', 'Línea de seguimiento')},
]);

const CATEGORY_OPTIONS = (t) => ([
    {value: CATEGORY_GOALS, label: t('config_columns_modal.categories.goals', 'Objetivos')},
    {value: CATEGORY_BUDGET, label: t('config_columns_modal.categories.budget', 'Presupuesto')},
    {value: CATEGORY_PERFORMANCE, label: t('config_columns_modal.categories.performance', 'Performance')},
    {value: CATEGORY_CONVERSIONS, label: t('config_columns_modal.categories.conversions', 'Conversiones')},
    {value: CATEGORY_VIDEO, label: t('config_columns_modal.categories.video', 'Video')},
    {value: CATEGORY_SOCIAL, label: t('config_columns_modal.categories.social', 'Social')},
    {value: CATEGORY_FUNDS, label: t('config_columns_modal.categories.funds', 'Saldos')},
    {value: CATEGORY_ATTRIBUTES, label: t('config_columns_modal.categories.attributes', 'Atributos')},
    {value: CATEGORY_STATUS, label: t('config_columns_modal.categories.status', 'Estado')},
    {value: CATEGORY_CUSTOM, label: t('config_columns_modal.categories.custom', 'Métricas personalizadas')},
]);

export function FacetSearch({searchQuery, onSearchChange, searchInputRef, t}) {
    return (
        <div className="ccm-search">
            <FaIcon iconName="search" className="ccm-search__icon"/>
            <input
                ref={searchInputRef}
                type="text"
                className="ccm-search__input"
                placeholder={t('config_columns_modal.search_placeholder', 'Buscar métricas, períodos, canales…')}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                autoFocus
            />
            {searchQuery && (
                <button type="button" className="ccm-search__clear" onClick={() => onSearchChange('')}>
                    <FaIcon iconName="times"/>
                </button>
            )}
        </div>
    );
}

export function FacetFilters({filters, onChange, t}) {
    const channelOptions = useMemo(() => CHANNEL_OPTIONS(t), [t]);
    const categoryOptions = useMemo(() => CATEGORY_OPTIONS(t), [t]);

    const handleChannel = (e) => onChange({...filters, channel: e.target.value === CHANNEL_ALL ? null : e.target.value});
    const handleCategory = (e) => {
        const value = e.target.value;
        onChange({...filters, category: value === '__ALL__' ? null : value});
    };

    return (
        <div className="ccm-filters">
            <label className="ccm-filters__group">
                <span className="ccm-filters__label">{t('config_columns_modal.filter_channel', 'Canal')}</span>
                <select
                    className="ccm-filters__select"
                    value={filters.channel || CHANNEL_ALL}
                    onChange={handleChannel}
                >
                    {channelOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </label>

            <label className="ccm-filters__group">
                <span className="ccm-filters__label">{t('config_columns_modal.filter_category', 'Categoría')}</span>
                <select
                    className="ccm-filters__select"
                    value={filters.category || '__ALL__'}
                    onChange={handleCategory}
                >
                    <option value="__ALL__">{t('config_columns_modal.all_categories', 'Todas')}</option>
                    {categoryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </label>

            {(filters.channel || filters.category) && (
                <div className="ccm-filters__group">
                    <span className="ccm-filters__label-spacer" aria-hidden="true">&nbsp;</span>
                    <button
                        type="button"
                        className="ccm-filters__clear"
                        onClick={() => onChange({channel: null, category: null, hasComparison: null})}
                    >
                        {t('config_columns_modal.clear_filters', 'Limpiar')}
                    </button>
                </div>
            )}
        </div>
    );
}

// Cambio 5: selector compacto de tableros guardados (reemplaza el TemplatesRail que ocupaba
// una columna fija). Un <select> con los nombres de las plantillas + acceso a "Crear plantilla".
// Replica el comportamiento de V1 (elegir un tablero ya guardado) sin comer espacio de pantalla.
export function TemplateSelect({columnsSets, activeColumnsSetId, onSelectSet, t}) {
    const sets = useMemo(() => {
        if (!columnsSets) return [];
        return Object.values(columnsSets).filter(
            (s) => s && s.name && s.name !== 'UNNAMED_UNSAVED_COLUMNS_SET'
        );
    }, [columnsSets]);

    const handleChange = (e) => {
        const value = e.target.value;
        if (value === '__NONE__') return;
        onSelectSet(value);
    };

    return (
        <div className="ccm-template-select">
            <span className="ccm-template-select__label">
                {t('config_columns_modal.template_selector_label', 'Plantilla')}
            </span>
            <div className="ccm-template-select__control">
                <FaIcon iconName="table" className="ccm-template-select__icon"/>
                <select
                    className="ccm-template-select__select"
                    value={activeColumnsSetId != null ? String(activeColumnsSetId) : '__NONE__'}
                    onChange={handleChange}
                    disabled={sets.length === 0}
                    title={t('config_columns_modal.template_selector_title', 'Elegí un tablero guardado para cargar sus columnas')}
                >
                    <option value="__NONE__">
                        {t('config_columns_modal.template_selector_none', 'Vista personalizada')}
                    </option>
                    {sets.map((set) => (
                        <option key={set.id} value={String(set.id)}>
                            {set.name} ({(set.columns || []).length})
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}

export function SaveAsInput({newSetName, onChange, onSave, onCancel, isSaving, t}) {
    const inputRef = useRef(null);
    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    return (
        <div className="ccm-save-input">
            <input
                ref={inputRef}
                type="text"
                className="ccm-save-input__field"
                placeholder={t('config_columns_modal.template_name_placeholder', 'Nombre de la plantilla')}
                value={newSetName}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSetName.trim() && !isSaving) onSave();
                    if (e.key === 'Escape') onCancel();
                }}
            />
            <button
                type="button"
                className="btn"
                onClick={onSave}
                disabled={!newSetName.trim() || isSaving}
            >
                {isSaving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
            </button>
            <button type="button" className="btn -alt" onClick={onCancel} disabled={isSaving}>
                {t('common.cancel', 'Cancelar')}
            </button>
        </div>
    );
}
